import {AuthenticatedLayout} from '@/shared/components/layouts/AuthenticatedLayout.tsx';
import {RootLayout} from '@/shared/components/layouts/RootLayout.tsx';
import {RouteErrorElement} from '@/shared/components/ui/ErrorBoundary.tsx';
import {Shimmer} from '@/shared/components/ui/loading/Shimmer.tsx';
import {Department} from '@/shared/types/common.ts';
import {ComponentType, lazy, Suspense} from 'react';
import {createBrowserRouter, Navigate} from 'react-router';
import {redirectIfAuthenticated, requireAuth, rootLoader} from './authLoader.ts';
import {LoginPageWrapper, RootRedirect} from './RootRedirect.tsx';
import {ROUTE_PATHS} from './routePaths.ts';
import {useAuthenticatedContext} from './useAuthenticatedContext.ts';

// =============================================
// LAZY-LOADED VIEW COMPONENTS
// =============================================

// Receptionist Views
const PagamentoView = lazy(
    () => import('@/features/receptionist/components/views/ClientsWithPendingPaymentsView')
);
const ReceptionistPendingPaymentsView = lazy(
    () => import('@/features/receptionist/components/views/PendingPaymentsView.tsx')
);
const PagamentoDetailView = lazy(
    () => import('@/features/receptionist/components/sheetViews/ClientPaymentDetailsSheetView.tsx')
);
const ClientesView = lazy(() => import('@/shared/components/views/ClientsView'));
const ClientDetailView = lazy(
    () => import('@/shared/components/sheetViews/ClientDetailsSheetView')
);
const EditarView = lazy(
    () => import('@/features/receptionist/components/views/EditClientsWithPaidServicesView')
);
const EditarDetailView = lazy(
    () =>
        import('@/features/receptionist/components/sheetViews/EditClientPaymentDetailsSheetView.tsx')
);
const SplitPaymentView = lazy(
    () => import('@/features/receptionist/components/sheetViews/SplitPaymentSheetView.tsx')
);
const BirthdayClientsSheetView = lazy(
    () => import('@/shared/components/sheetViews/BirthdayClientsSheetView.tsx')
);
const InactiveClientsSheetView = lazy(
    () => import('@/shared/components/sheetViews/InactiveClientsSheetView.tsx')
);

// Employee Main Views
const EmployeeDailyServicesView = lazy(
    () => import('@/features/employee/components/views/EmployeeDailyServicesView')
);
const EmployeeWeeklyMonthlyEarningsView = lazy(
    () => import('@/features/employee/components/views/EmployeeWeeklyMonthlyEarningsView')
);
const EmployeePendingPaymentsView = lazy(
    () => import('@/features/employee/components/views/EmployeePendingPaymentsView.tsx')
);

// Employee Sheet Views
const EmployeePeriodServicesSheetView = lazy(
    () => import('@/features/employee/components/sheetViews/EmployeePeriodServicesSheetView')
);
const EmployeeDayServicesSheetView = lazy(
    () => import('@/features/employee/components/sheetViews/EmployeeDayServicesSheetView')
);
const EmployeePendingClientServicesSheetView = lazy(
    () => import('@/features/employee/components/sheetViews/EmployeePendingClientServicesSheetView.tsx')
);

// Owner Views
const OwnerSaldoView = lazy(() => import('@/features/owner/components/views/OwnerSaldoView.tsx'));
const OwnerDiarioView = lazy(() => import('@/features/owner/components/views/OwnerDiarioView.tsx'));
const OwnerPeriodServicesView = lazy(
    () => import('@/features/owner/components/views/OwnerPeriodServicesView.tsx')
);
const OwnerComissaoView = lazy(
    () => import('@/features/owner/components/views/OwnerComissaoView.tsx')
);
const ProfessionalDetailView = lazy(
    () => import('@/features/owner/components/views/ProfessionalDetailView.tsx')
);
const OwnerDayServicesView = lazy(
    () => import('@/features/owner/components/views/OwnerDayServicesView.tsx')
);
const OwnerFluxoView = lazy(() => import('@/features/owner/components/views/OwnerFluxoView.tsx'));
const OwnerPendingPaymentsView = lazy(
    () => import('@/features/owner/components/views/OwnerPendingPaymentsView.tsx')
);
const VaultDetailView = lazy(() => import('@/features/owner/components/views/VaultDetailView.tsx'));
const VaultDayServicesView = lazy(
    () => import('@/features/owner/components/views/VaultDayServicesView.tsx')
);
const OwnerPendingClientServicesSheetView = lazy(
    () => import('@/features/owner/components/sheetViews/OwnerPendingClientServicesSheetView.tsx')
);

// Audit Trail View
const AuditTrailView = lazy(() => import('@/features/audit/components/views/AuditTrailView.tsx'));

// =============================================
// SUSPENSE WRAPPER FOR LAZY COMPONENTS
// =============================================

const LazyView = ({children}: {children: React.ReactNode}) => (
    <Suspense
        fallback={
            <div className="view-container p-4">
                <Shimmer count={4} type="card" />
            </div>
        }
    >
        {children}
    </Suspense>
);

// =============================================
// CONTEXT INJECTION HOCs
// =============================================

/**
 * Higher-order component that injects employeeId and employeeName from outlet context.
 * Used for employee views that require these props.
 */
const withEmployeeContext = <P extends {employeeId: string; employeeName: string}>(
    Component: ComponentType<P>
) => {
    const WrappedComponent = (props: Omit<P, 'employeeId' | 'employeeName'>) => {
        const {employeeId, employeeName} = useAuthenticatedContext();
        return <Component {...(props as P)} employeeId={employeeId} employeeName={employeeName} />;
    };
    WrappedComponent.displayName = `withEmployeeContext(${Component.displayName || Component.name})`;
    return WrappedComponent;
};

/**
 * Higher-order component that injects employeeId, employeeName and employeeDepartment from outlet context.
 * Used for employee views that require all props.
 */
const withEmployeeFullContext = <
    P extends {employeeId: string; employeeName: string; employeeDepartment: Department | null},
>(
    Component: ComponentType<P>
) => {
    const WrappedComponent = (
        props: Omit<P, 'employeeId' | 'employeeName' | 'employeeDepartment'>
    ) => {
        const {employeeId, employeeName, employeeDepartment} = useAuthenticatedContext();
        return (
            <Component
                {...(props as P)}
                employeeId={employeeId}
                employeeName={employeeName}
                employeeDepartment={employeeDepartment}
            />
        );
    };
    WrappedComponent.displayName = `withEmployeeFullContext(${Component.displayName || Component.name})`;
    return WrappedComponent;
};

// Employee view components with context injection
const EmployeeDailyServicesViewWithContext = withEmployeeContext(EmployeeDailyServicesView);
const EmployeePendingPaymentsViewWithContext = withEmployeeContext(EmployeePendingPaymentsView);
const EmployeePendingClientServicesSheetViewWithContext = withEmployeeContext(
    EmployeePendingClientServicesSheetView
);
const EmployeeWeeklyMonthlyEarningsViewWithContext = withEmployeeFullContext(
    EmployeeWeeklyMonthlyEarningsView
);

/**
 * Application router configuration using React Router v7 Data Mode.
 *
 * Auth Strategy:
 * - Loaders check session validity (fast, sync check from localStorage)
 * - Components check role (async, from AuthContext/Supabase)
 *
 * Code Splitting:
 * - Views are lazy-loaded for smaller initial bundle
 * - Suspense fallback shows shimmer skeleton during load
 *
 * Route structure:
 * - / (RootLayout with providers)
 *   - / (RootRedirect - redirects to role-based dashboard)
 *   - /login (LoginPage - redirects if already authenticated)
 *   - /recepcionista (AuthenticatedLayout for receptionist)
 *   - /profissional (AuthenticatedLayout for employee)
 *   - /admin (AuthenticatedLayout for owner)
 */
export const router = createBrowserRouter([
    {
        path: ROUTE_PATHS.ROOT,
        element: <RootLayout />,
        errorElement: <RouteErrorElement />,
        children: [
            // Root redirect based on role
            {
                index: true,
                loader: rootLoader,
                element: <RootRedirect />,
            },

            // Login page - redirect away if already authenticated
            {
                path: 'login',
                loader: redirectIfAuthenticated,
                element: <LoginPageWrapper />,
            },

            // Receptionist routes - require auth
            {
                path: 'recepcionista',
                loader: requireAuth,
                element: <AuthenticatedLayout allowedRole="receptionist" />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="pagamento" replace />,
                    },
                    {
                        path: 'pagamento',
                        element: (
                            <LazyView>
                                <PagamentoView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamento/:clientId',
                        element: (
                            <LazyView>
                                <PagamentoDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamento/:clientId/dividir',
                        element: (
                            <LazyView>
                                <SplitPaymentView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes',
                        element: (
                            <LazyView>
                                <ReceptionistPendingPaymentsView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes/:clientId',
                        element: (
                            <LazyView>
                                <PagamentoDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes',
                        element: (
                            <LazyView>
                                <ClientesView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/:clientId',
                        element: (
                            <LazyView>
                                <ClientDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/aniversariantes',
                        element: (
                            <LazyView>
                                <BirthdayClientsSheetView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/inativos',
                        element: (
                            <LazyView>
                                <InactiveClientsSheetView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'editar',
                        element: (
                            <LazyView>
                                <EditarView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'editar/:clientId',
                        element: (
                            <LazyView>
                                <EditarDetailView />
                            </LazyView>
                        ),
                    },
                    // Audit Trail (available for all roles)
                    {
                        path: 'historico',
                        element: (
                            <LazyView>
                                <AuditTrailView />
                            </LazyView>
                        ),
                    },
                ],
            },

            // Employee routes - require auth
            {
                path: 'profissional',
                loader: requireAuth,
                element: <AuthenticatedLayout allowedRole="employee" />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="resumo" replace />,
                    },
                    {
                        path: 'resumo',
                        element: (
                            <LazyView>
                                <EmployeeDailyServicesViewWithContext />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'resumo/periodo/:periodKey',
                        element: (
                            <LazyView>
                                <EmployeePeriodServicesSheetView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'fechamento',
                        element: (
                            <LazyView>
                                <EmployeeWeeklyMonthlyEarningsViewWithContext />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'fechamento/dia',
                        element: (
                            <LazyView>
                                <EmployeeDayServicesSheetView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes',
                        element: (
                            <LazyView>
                                <EmployeePendingPaymentsViewWithContext />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes/:clientId',
                        element: (
                            <LazyView>
                                <EmployeePendingClientServicesSheetViewWithContext />
                            </LazyView>
                        ),
                    },
                    // Audit Trail (available for all roles)
                    {
                        path: 'historico',
                        element: (
                            <LazyView>
                                <AuditTrailView />
                            </LazyView>
                        ),
                    },
                ],
            },

            // Owner routes - require auth
            {
                path: 'admin',
                loader: requireAuth,
                element: <AuthenticatedLayout allowedRole="owner" />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="clientes" replace />,
                    },
                    {
                        path: 'saldo',
                        element: (
                            <LazyView>
                                <OwnerSaldoView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'diario',
                        element: (
                            <LazyView>
                                <OwnerDiarioView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'diario/periodo/:periodKey',
                        element: (
                            <LazyView>
                                <OwnerPeriodServicesView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'comissao',
                        element: (
                            <LazyView>
                                <OwnerComissaoView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'comissao/profissional/:employeeId',
                        element: (
                            <LazyView>
                                <ProfessionalDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'comissao/profissional/:employeeId/dia',
                        element: (
                            <LazyView>
                                <OwnerDayServicesView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'fluxo',
                        element: (
                            <LazyView>
                                <OwnerFluxoView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'fluxo/cofre/:vaultId',
                        element: (
                            <LazyView>
                                <VaultDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'fluxo/cofre/:vaultId/dia',
                        element: (
                            <LazyView>
                                <VaultDayServicesView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes',
                        element: (
                            <LazyView>
                                <OwnerPendingPaymentsView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'pagamentos-pendentes/:clientId',
                        element: (
                            <LazyView>
                                <OwnerPendingClientServicesSheetView />
                            </LazyView>
                        ),
                    },
                    // Client routes (reusing receptionist components)
                    {
                        path: 'clientes',
                        element: (
                            <LazyView>
                                <ClientesView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/:clientId',
                        element: (
                            <LazyView>
                                <ClientDetailView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/aniversariantes',
                        element: (
                            <LazyView>
                                <BirthdayClientsSheetView />
                            </LazyView>
                        ),
                    },
                    {
                        path: 'clientes/inativos',
                        element: (
                            <LazyView>
                                <InactiveClientsSheetView />
                            </LazyView>
                        ),
                    },
                    // Audit Trail
                    {
                        path: 'historico',
                        element: (
                            <LazyView>
                                <AuditTrailView />
                            </LazyView>
                        ),
                    },
                ],
            },

            // Catch-all redirect to root
            {
                path: '*',
                element: <Navigate to={ROUTE_PATHS.ROOT} replace />,
            },
        ],
    },
]);
